// articles/NassauSetup.js
// Game guide #1 (Oct 4, 2026): a Nassau in TTO, start to finish. The first guide written
// for a WEEKLY round first (the event steps are a short note at the end), per Dennis:
// group games get game guides, tournament formats keep their event guides.
// Written from the live code: the Nassau card in Games Setup (App.js NassauGamePanel —
// Matches, Strokes, Presses), the engine (gameEngines/nassau.js), the press prompt on the
// hole screen (NassauPressPrompt) and the Results tab (NassauMatches). Button names are
// quoted exactly as they appear, so if a label changes in App.js, change it here too.
//
// Linked from inside the app: 📖 Guides → Game guides. Public, so prospective organizers
// can read it before they sign up.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-nassau-golf-game');

const FAQS = [
  {
    q: 'What happens when a nine ends all square?',
    a: 'That bet is a push and no money changes hands for it. The same goes for a press or the overall that ends all square.',
  },
  {
    q: 'Can I press the overall?',
    a: 'No. Presses in TTO are on the nines only: the front, the back, and presses of those presses. The overall is always one bet.',
  },
  {
    q: 'How many presses can there be?',
    a: 'As many as the match produces. A press starts when a side goes 2 down on the latest bet of a nine, and that press can itself be pressed when the side goes 2 down on it. Only the latest bet of a nine can be pressed, so presses come one after another, never several at once.',
  },
  {
    q: 'A player tapped Press by mistake. Can I undo it?',
    a: 'Not yet from the app. Tell us and we will remove it; then re-finalize so the money follows. To avoid it, choose automatic presses or no presses for groups that would rather not be asked.',
  },
  {
    q: 'Can we play a Nassau on nine holes?',
    a: 'No. A Nassau is the front, the back and the overall, so it needs all 18 holes. TTO won’t save a Nassau on a round set up as a nine.',
  },
  {
    q: 'Can I run a Nassau with Captain’s Choice?',
    a: 'No. A Nassau needs each player’s own score on every hole, and a Captain’s Choice round has one score per team. It runs fine beside Stroke Play, Best Ball, skins, Dots and the other games that keep each player’s own card.',
  },
  {
    q: 'We shotgun-started. Which holes does a press cover?',
    a: 'The rest of that nine in the order the group plays it. A group that starts on the 5th plays the front nine as 5 to 9 and then, later, 1 to 4. A press that starts on the 7th covers 7, 8 and 9, then 1 to 4 when the group gets there.',
  },
  {
    q: 'Is the Nassau on the event leaderboard?',
    a: 'No. The live leaderboard and TV view show the main game. Players follow their Nassau matches in the Results tab of their app.',
  },
];

export default function NassauSetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Nassau
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        A Nassau is three bets in one: the <strong>front 9</strong>, the <strong>back 9</strong> and
        the <strong>overall 18</strong>, each for the same amount. Every bet is a match. Each hole is
        won by the lower score or halved, and the side that wins more holes wins the bet. A bet that
        ends all square pays nothing. When a side falls 2 down on a nine it can <strong>press</strong>:
        start a new bet for the same amount that runs to the end of that nine.
      </P>
      <P>
        This guide sets up a Nassau for your weekly round, from the first click to the settlement.
        Running one in an event? The steps are the same; see the note at the end.
      </P>

      <StepTracker
        storageKey="tto_guide_nassau_steps"
        steps={[
          'Groups on the Pairings page',
          'Add the Nassau',
          'Who’s in',
          'Play & press',
          'Results & settle',
        ]}
        note="Steps 1 to 3 happen before the round; 4 and 5 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Nassau needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Nassau isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          <strong>18 holes only.</strong> A Nassau can&rsquo;t be saved on a round set up as a nine.
        </LI>
        <LI>
          Decide with your group:
          <UL>
            <LI><strong>Matches:</strong> every man for himself, or 2 vs 2 best ball.</LI>
            <LI><strong>Strokes:</strong> net off the low man, net on full course handicap, or gross.</LI>
            <LI><strong>Presses:</strong> automatic, ask the player, or none.</LI>
            <LI><strong>The stake:</strong> what each bet is worth, $5 in a &ldquo;$5 Nassau&rdquo;.</LI>
          </UL>
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Set the groups on the Pairings page" when="Once you know who's playing">
        <P>
          A Nassau is played inside each group: players only have matches against players in their
          own group.
        </P>
        <UL>
          <LI>
            Open the <strong>Pairings</strong> page for the tee time, build the groups as you normally
            do, and click <Btn>💾 Save Pairings</Btn>.
          </LI>
          <LI>
            <strong>Playing 2 vs 2?</strong> In each foursome, <strong>the first two players listed
            play the last two</strong>. Arrange each group so partners are listed together before you
            save. A group that isn&rsquo;t four players has no 2 vs 2 match; the Results tab says so.
          </LI>
          <LI>
            No pairings saved? TTO treats everyone in the game as one group, which is fine for a
            single foursome.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Add the Nassau" when="Before the round">
        <Heads>
          <strong>On a net Nassau, every player needs a complete profile.</strong> Each
          player&rsquo;s profile must include a <strong>preferred tee</strong> and a{' '}
          <strong>Handicap Index</strong>, so that his course handicap can be worked out. Check them
          in Admin &rarr; Players. A player with no Handicap Index can&rsquo;t opt himself in, and TTO warns you if you put him in; until he adds one he plays as scratch.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Nassau</strong>.
          </LI>
          <LI>
            <strong>Wager:</strong> the amount of <em>each bet</em>: the front, the back, the overall
            and every press. $0 makes it results only: TTO keeps the matches and settles no money.
          </LI>
          <LI>
            <strong>Matches:</strong>
            <UL>
              <LI>
                <Btn>Every player vs every player in his group</Btn>: each player has a match with
                every other player in his group (three matches each in a foursome).
              </LI>
              <LI>
                <Btn>2 vs 2 best ball</Btn>: in each foursome the first two play the last two, and
                the team&rsquo;s better score on each hole counts.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Strokes:</strong>
            <UL>
              <LI>
                <Btn>Net — off the low man</Btn>: in each match, the lowest course handicap plays
                off scratch and the others get the difference. In 2 vs 2 the low man is the lowest
                of the four. This is the default, and how most groups play a Nassau.
              </LI>
              <LI>
                <Btn>Net — full course handicap</Btn>: every player gets all of his own strokes.
              </LI>
              <LI>
                <Btn>Gross</Btn>: no strokes.
              </LI>
            </UL>
            Strokes fall on the hardest holes of each player&rsquo;s own tee, with a second stroke on
            the hardest holes for more than 18.
          </LI>
          <LI>
            <strong>Presses:</strong>
            <UL>
              <LI>
                <Btn>Automatic at 2 down</Btn>: a new bet starts by itself on the next hole when a
                side goes 2 down on a nine, and again when it goes 2 down on that press.
              </LI>
              <LI>
                <Btn>Ask the player at 2 down</Btn>: the player who goes 2 down sees a{' '}
                <strong>Press?</strong> prompt on his hole screen and decides. This is the default.
              </LI>
              <LI>
                <Btn>No presses</Btn>: just the three bets.
              </LI>
            </UL>
            Presses are on the nines only, never the overall, and each runs to the end of its nine.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={3} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new Nassau starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Matches are made only between players who are in. A player who sits out doesn&rsquo;t
            change anyone else&rsquo;s matches, except in 2 vs 2, where his foursome loses its match.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={4} title="Play, and press when you're 2 down" when="During the round">
        <H3>Keeping score</H3>
        <UL>
          <LI>
            Each player enters his scores hole by hole, or one player keeps the card for his group
            with <strong>+ Score for another player</strong>.
          </LI>
          <LI>
            A hole of a match counts once every player in that match has a score on it.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey.
          </LI>
        </UL>
        <H3>Pressing (when presses are set to ask the player)</H3>
        <UL>
          <LI>
            When a player on the card goes 2 down on a nine, a prompt appears just above the{' '}
            <strong>Next</strong> button as he arrives at the next hole, for example:{' '}
            <em>&ldquo;You&rsquo;re 2 down to Bob on the front. Press?&rdquo;</em>
          </LI>
          <LI>
            <Btn>Press — $5</Btn> starts the press from that hole to the end of the nine.{' '}
            <Btn>Not now</Btn> turns it down; the prompt comes back only if he falls further behind
            on that bet.
          </LI>
          <LI>
            The player keeping the card can answer for a player in his group.
          </LI>
          <LI>
            Nothing else of the Nassau shows on the hole screen, so scoring stays as simple as always.
          </LI>
        </UL>
        <H3>Following the matches</H3>
        <UL>
          <LI>
            Players follow their matches in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong> (the Results tab). Each match lists the front, the back, the
            overall and any presses, with the status as they play (&ldquo;2 up thru 6&rdquo;) and the
            result at the end.
          </LI>
        </UL>
      </Step>

      <Step n={5} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each bet won collects the wager. Each pair of players settles the net of all their bets,
            and in 2 vs 2 each player settles with one player on the other side.
          </LI>
          <LI>
            Every player gets a statement, and you can email everyone their results: for each person
            he settles with, &ldquo;2 bets won, 1 lost × $5.00&rdquo;, and each match bet by bet.
          </LI>
          <LI>
            <strong>A card needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn>, correct it in Post-Round Score Entry, then finalize again.
          </LI>
        </UL>
        <H3>Examples, at $5 a bet</H3>
        <UL>
          <LI>
            <strong>No presses:</strong> Al wins the front, they halve the back, Al wins the overall.
            Bob pays Al $10.
          </LI>
          <LI>
            <strong>With a press:</strong> Bob is 2 down after the 4th and presses. Al wins the front
            by one, but Bob wins the press (holes 5 to 9). The front and the press cancel out; the
            back and overall decide the rest.
          </LI>
          <LI>
            <strong>2 vs 2:</strong> Al and Cal win the front and the overall and lose the back. Each
            of them collects $5 from one player on the other side.
          </LI>
        </UL>
      </Step>

      <H2>Running a Nassau in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments).
        At the step where you add the main game, tick <strong>Nassau</strong> beside it and set it up
        as in step 2 above. Matches are made within each saved group, so save the pairings before the
        round. The event&rsquo;s live leaderboard shows the main game; players follow the Nassau in
        their app.
      </P>

      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score is
          entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the matches,
          presses and money follow the new settings.
        </LI>
      </UL>

      <FAQ id="nassau-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
